import { Hero } from '@/components/Hero'
import { statamicGraphqlEndpoint } from '@/globals/globals'
import { createBillingCheckout } from '@/lib/createStripeBillingSession'
import { createStripeCheckout } from '@/lib/createStripeCheckoutSession'
import { getCurrentUsersPlan } from '@/lib/getCurrentUsersPlan'
import { UserContext } from '@/lib/UserContext'
import { MembershipPlan } from '@/types/membershipPlan'
import { MembershipTerm } from '@/types/membershipTerm'
import { User } from '@/types/user'
import {
  Box,
  Button,
  Container,
  Flex,
  Link,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import { captureEvent } from '@sentry/nextjs'
import request, { gql } from 'graphql-request'
import { GetStaticProps } from 'next'
import NextImage from 'next/legacy/image'
import { useRouter } from 'next/router'
import React, { useContext, useState } from 'react'
import { Discount } from './dashboard/discounts'
import { uniqBy } from 'lodash'
import { trackEvent } from 'modules/insights/Insights'

export const getStaticProps: GetStaticProps = async () => {
  const {
    entries: { data: plans },
  } = await request(
    statamicGraphqlEndpoint,
    gql`
      query planQuery {
        entries(collection: "membership-plan", sort: "order") {
          data {
            title
            slug
            ... on Entry_MembershipPlan_EntryMembershipPlan {
              id
              benefits {
                benefit
                highlight
                yearly_only
              }
              monthly_cost
              monthly_stripe_plan_id
              monthly_stripe_plan_id_cad
              yearly_cost
              yearly_stripe_plan_id
              yearly_stripe_plan_id_cad
            }
          }
        }
      }
    `
  )

  const {
    entries: { data: logoResponse },
  } = await request(
    statamicGraphqlEndpoint,
    gql`
      query logoQuery {
        entries(collection: "discounts") {
          data {
            ... on Entry_Discounts_Discounts {
              id
              visible_to {
                handle
              }
              logo {
                url
              }
            }
          }
        }
      }
    `
  )

  // Filter logos that are not visible to builder
  const logosFiltered = logoResponse.filter((entry: Partial<Discount>) => {
    if (!entry.stop_on) return true
    let today = Date.now()
    let stopTime = Date.parse(entry.stop_on)
    return stopTime > today
  })

  // Remove duplicate logos
  const logos = uniqBy(logosFiltered, 'logo.url')

  return {
    props: { plans, logos },
    revalidate: 60,
  }
}

type UserStatus = 'signedOut' | 'enthusiast' | 'apprentice' | 'builder'

const isUserStatus = (status?: string): status is UserStatus =>
  status
    ? ['signedOut', 'enthusiast', 'apprentice', 'builder'].includes(status)
    : false

// Text options for buttons
const buttonText = new Map<UserStatus, { [key: string]: string }>([
  [
    'signedOut',
    { enthusiast: 'Start for free', apprentice: 'Sign Up', builder: 'Sign Up' },
  ],
  [
    'enthusiast',
    { enthusiast: 'Current Plan', apprentice: 'Upgrade', builder: 'Upgrade' },
  ],
  [
    'apprentice',
    { enthusiast: 'Downgrade', apprentice: 'Current Plan', builder: 'Upgrade' },
  ],
  [
    'builder',
    {
      enthusiast: 'Downgrade',
      apprentice: 'Downgrade',
      builder: 'Current Plan',
    },
  ],
])

export default function MembershipPage(props: any): JSX.Element {
  const [term, setTerm] = useState<MembershipTerm>('yearly')
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const toast = useToast()
  const userData = useContext(UserContext)
  let user: User = userData()
  let currentPlan = user && getCurrentUsersPlan(user, { format: 'slug' })
  const router = useRouter()

  // Process logic for scenarios
  const handleSubscribeClick = (plan: MembershipPlan): void => {
    setIsProcessing(true)

    if (!user) {
      const query =
        plan === 'enthusiast'
          ? undefined
          : {
              redirectTo: `/checkout/confirm?plan=${plan}&term=${term}`,
            }

      router.push({
        pathname: '/register',
        query,
      })
      return
    }

    // If enthusiast, go to the checkout page
    if (currentPlan === 'enthusiast') {
      createStripeCheckout({ plan, term, user })
        .catch(error => {
          toast({
            title: 'Error',
            description: error.error?.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
          captureEvent(error)
        })
        .finally(() => setIsProcessing(false))
      return
    }

    // If paid member redirect to billing to end subscription
    if (
      (currentPlan === 'apprentice' || currentPlan === 'builder') &&
      user.stripe_id
    ) {
      createBillingCheckout({ stripeID: user.stripe_id })
        .then(url => (window.location.href = url))
        .catch(error => {
          toast({
            title: error.message || 'Unknown Error',
            description: <Link href='/contact'>Contact Support ›</Link>,
            status: 'error',
            isClosable: true,
            duration: null,
          })
          captureEvent(error, {
            data: ['Error redirecting to payment.', user?.uuid, plan, term],
          })
        })
        .finally(() => setIsProcessing(false))
    }

    // Track event
    trackEvent('Signup Click', { plan, term, user: user.uuid })
  }

  // Set text for button based on membership plan
  const getButtonText = (plan: MembershipPlan): string | undefined => {
    const defaultText = 'Register'

    if (!user) return defaultText

    const currentPlan = getCurrentUsersPlan(user, { format: 'slug' })

    // Check if currentPlan is key of buttonText
    if (isUserStatus(currentPlan) && buttonText.has(currentPlan)) {
      const buttonPlan = buttonText.get(currentPlan)

      // Ensure buttonPlan is a string
      return buttonPlan ? buttonPlan[plan] : defaultText
    }

    return defaultText
  }

  return (
    <>
      <Hero
        heading='Showcase your projects'
        label='Get access to membership perks'>
        <Box maxWidth='2xl' mt={3} mx='auto'>
          <Text>
            Recurring payment. Cancel at any time. We may update perks from time
            to time. Memberships are non-refundable.
          </Text>
        </Box>
      </Hero>
      <Container maxWidth='6xl' py={24}>
        <Flex justifyContent='center'>
          <Flex
            justifyContent='center'
            mb={12}
            backgroundColor='gray.900'
            borderRadius='3xl'
            overflow='hidden'>
            <Button
              onClick={() => setTerm('monthly')}
              colorScheme={term === 'monthly' ? 'black' : 'gray'}
              borderRadius={0}>
              Monthly
            </Button>
            <Button
              onClick={() => setTerm('yearly')}
              colorScheme={term === 'yearly' ? 'black' : 'gray'}
              borderRadius={0}>
              Yearly
            </Button>
          </Flex>
        </Flex>
        <SimpleGrid
          columns={{ base: 1, lg: 3 }}
          spacing={{ base: 20, lg: 8, xl: 16 }}>
          {props.plans.map((plan: any, i: number) => (
            <Stack key={i} spacing={4} maxWidth='xl' mx='auto'>
              <Stack spacing={0} textAlign='center'>
                <Text textTransform='uppercase'>Level {i + 1}</Text>
                <Text fontSize='display3' fontWeight='bold'>
                  {plan.title}
                </Text>
                <Text fontWeight='bold'>
                  {term === 'monthly'
                    ? `${plan.monthly_cost} / month`
                    : `${plan.yearly_cost} / year`}
                </Text>
              </Stack>
              {plan.benefits.map(
                (
                  benefit: {
                    benefit: string
                    highlight: boolean
                    yearly_only: string
                  },
                  i: number
                ) => {
                  // If the benefit is only for yearly, and the term is monthly, skip it
                  if (benefit.yearly_only && term === 'monthly') return null

                  return (
                    <React.Fragment key={i}>
                      {benefit.highlight === true ? (
                        <Flex justifyContent='center'>
                          <Box
                            dangerouslySetInnerHTML={{
                              __html: benefit.benefit,
                            }}
                            backgroundColor='yellow.700'
                            textAlign='center'
                            px={4}
                            py={1.5}
                            display='block'
                            lineHeight='1.25'
                          />
                        </Flex>
                      ) : (
                        <Box
                          dangerouslySetInnerHTML={{ __html: benefit.benefit }}
                        />
                      )}
                      <Box>{benefit.highlight}</Box>
                    </React.Fragment>
                  )
                }
              )}
              <Box textAlign='center' pt={4}>
                <Button
                  onClick={() => handleSubscribeClick(plan.slug)}
                  colorScheme='red'
                  mx='auto'
                  size='lg'
                  isLoading={isProcessing}
                  spinner={<Spinner />}
                  disabled={currentPlan == plan.slug}>
                  {getButtonText(plan.slug)}
                </Button>
              </Box>
            </Stack>
          ))}
        </SimpleGrid>
      </Container>
      <Box id='discount-partners' pt={12} pb={28}>
        <Text textAlign='center' fontWeight='bold' mb={8}>
          Builders Have Access to Exclusive Discounts on Tools & Car Parts
          Currently Available From:
        </Text>
        <Flex flexWrap='wrap' justifyContent='center'>
          {props.logos?.map((logo: any, i: number) =>
            logo.logo?.url ? (
              <Box key={i} h={20} p={4}>
                <NextImage
                  src={logo.logo.url}
                  width={150}
                  height={75}
                  objectFit='contain'
                  alt={' '}
                />
              </Box>
            ) : null
          )}
        </Flex>
      </Box>
    </>
  )
}
